import React, { useState, useEffect } from 'react'
import { Map } from '../../core/maps'

const CENTER_POSITION = {
  lat: 41.9405,
  lng: 2.3163,
}

const DEFAULT_ZOOM = 20

/**
 * @typedef {object} Position
 * @property {number} lat - La latitud de la posición.
 * @property {number} lng - La longitud de la posición.
 */

/**
 * @typedef {object} MapPickerProps
 * @property {Position} position - La posición actual del marcador.
 * @property {function(Position): void} setPosition - La función callback para actualizar la posición cuando se mueve el marcador.
 * @property {Position} [center=CENTER_POSITION] - La posición central inicial del mapa (Barcelona por defecto).
 * @property {number} [zoom=20] - El nivel de zoom inicial del mapa.
 */

/**
 * Un componente de selector de ubicación en mapa usando Google Maps.
 * 
 * Renderiza un mapa interactivo con un marcador arrastrable que permite al usuario
 * seleccionar una ubicación geográfica. El mapa se muestra en vista híbrida (satélite + calles)
 * con los puntos de interés (POI) ocultos para mayor claridad.
 * 
 * **Nota:** Este componente requiere que Google Maps API esté cargada y disponible globalmente.
 * 
 * @component
 * @example
 * const MyLocationPicker = () => {
 *   const [position, setPosition] = useState({ lat: 41.9405, lng: 2.3163 });
 * 
 *   return (
 *     <div style={{ height: '500px', width: '100%' }}>
 *       <MapPicker
 *         position={position}
 *         setPosition={setPosition}
 *         center={{ lat: 41.9405, lng: 2.3163 }}
 *         zoom={18}
 *       />
 *     </div>
 *   );
 * };
 * 
 * @param {MapPickerProps} props - Las props del componente MapPicker.
 */
export const MapPicker = ({
  position,
  setPosition,
  center = CENTER_POSITION,
  zoom = DEFAULT_ZOOM,
}) => {
  const [marker, setMarker] = useState()
  useEffect(() => {
    if (marker) {
      marker.setPosition(position)
    }
  }, [position]) // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Map
      id="test"
      options={{
        center,
        zoom,
        mapTypeId: 'hybrid',
        tilt: 0,
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }],
          },
        ],
        streetViewControl: false,
      }}
      onMapLoad={(map) => {
        var marker = new window.google.maps.Marker({
          position: position || center,
          map,
          draggable: true,
        })
        setMarker(marker)
        setPosition(center)
        window.google.maps.event.addListener(marker, 'dragend', (e) => {
          setPosition({ lat: e.latLng.lat(), lng: e.latLng.lng() })
        })
      }}
    />
  )
}

export default MapPicker
